const storeCookieName = 'pxg_preferred_store';
const cookieDurationDays = 30;
// Cookies without this version predate the store-geolocation `locationImprecise` fix: they can
// hold `n/a` for shoppers MaxMind could only place at country level, or a store matched from the
// center of the US. They're cleared on load and looked up again. See docs/adr/0002.
const storeCookieVersion = 2;
const storeGeolocationEndpoint = '/apps/pxg/v1/store-geolocation';

function getStoreCookie() {
  const value = document.cookie.split('; ').find((row) => row.startsWith(`${storeCookieName}=`));
  return value ? decodeURIComponent(value.split('=')[1]) : null;
}

// Pass `days: null` for a session cookie.
function setStoreCookie(objectValue, days = cookieDurationDays) {
  let expires = '';
  if (days !== null) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `expires=${date.toUTCString()};`;
  }
  const cookieValue = encodeURIComponent(JSON.stringify(objectValue));
  document.cookie = `${storeCookieName}=${cookieValue};${expires}path=/`;
  registerPreferredStore(objectValue);
  document.dispatchEvent(new CustomEvent('preferred-store:change', { detail: { store: objectValue } }));
}

function capturePreferredStoreEvent(eventName, properties) {
  window.posthog?.capture?.(eventName, properties);
}

// PostHog super properties, so every event (product_viewed, Local Fitter Callout, fitting events)
// carries the shopper's store. window.posthog is a no-op stub when PostHog is disabled.
function registerPreferredStore(storeData, status) {
  const hasStore = Boolean(storeData?.name && storeData.name !== 'n/a');
  window.posthog?.register?.({
    preferred_store_name: hasStore ? storeData.name : null,
    preferred_store_source: hasStore ? storeData.source || null : null,
    preferred_store_status: status || (hasStore ? 'found' : storeData?.reason || 'none'),
  });
}

function clearStoreCookie() {
  document.cookie = `${storeCookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

function getStoreFromCookie() {
  const cookie = getStoreCookie();
  try {
    return cookie ? JSON.parse(cookie) : null;
  } catch (e) {
    console.warn('PXG Stores: failed to parse store cookie:', e);
    return null;
  }
}

// `store.fields` holds ad-hoc named values (Salesperson, Salesperson image, etc.) -
// not every store has every field, so this returns '' rather than undefined when absent,
// letting callers tell "checked, not present" apart from "never checked" (older cookie).
function getStoreFieldValue(store, fieldName) {
  const fields = Array.isArray(store.fields) ? store.fields : [];
  const field = fields.find((f) => f && f.name === fieldName);
  return field && typeof field.value === 'string' ? field.value.trim() : '';
}

// Shared shape for the pxg_preferred_store cookie payload - also used by
// src/components/Acuity/SchedulerApp.jsx (via window.getStoreCookiePayload, exposed below)
// when a customer explicitly selects a store. Keep these in sync - see docs/adr/0002.
// `source` records how the store was chosen: 'ip', 'browser' (shared location) or 'selected'.
function getStoreCookiePayload(store, source = 'ip') {
  return {
    v: storeCookieVersion,
    source,
    name: store.name,
    latitude: parseFloat(store.lat),
    longitude: parseFloat(store.lng),
    salespersonName: getStoreFieldValue(store, 'Salesperson'),
    salespersonImage: getStoreFieldValue(store, 'Salesperson image'),
    phone: typeof store.phone === 'string' ? store.phone : '',
    email: typeof store.email === 'string' ? store.email : '',
  };
}

// `reason` is 'none' when the shopper's location is known but no store is within range, or
// 'imprecise' when their IP only places them at country level.
function getNoStoreCookiePayload(reason) {
  return { v: storeCookieVersion, name: 'n/a', latitude: '', longitude: '', reason };
}

// Update the Fitting Locations text and link in upper nav
function updateLocationUI(storeName, storeLat, storeLong, closestStoreGeolocated = false) {
  if (!window.featureToggle.preferredStoreUXEnabled) {
    // console.log('PXG Stores: disabled');
    return;
  }
  const locationLink = document.querySelector('.location-link');
  if (locationLink && storeLat && storeLong) {
    const baseUrl = locationLink.href.split('#')[0].split('?')[0];
    locationLink.href = `${baseUrl}?search=${encodeURIComponent(`${storeLat},${storeLong}`)}#locations-component`;

    locationLink.classList.remove('text-fairway');
    locationLink.classList.add('text-sunset');

    const textFields = locationLink.querySelectorAll('.locations-text');
    textFields.forEach((field) => {
      field.textContent = storeName;

      field.classList.remove('text-fairway');
      field.classList.add('text-sunset');
    });
  }
  if (locationLink && closestStoreGeolocated) {
    const icon = locationLink.querySelector('svg');
    const textFields = locationLink.querySelectorAll('.locations-text');

    if (icon) {
      icon.classList.add('animate-pulse');
    }
    textFields.forEach((field) => {
      field.classList.add('animate-pulse');
    });

    // remove the animation class after 2 seconds
    setTimeout(() => {
      if (icon) {
        icon.classList.remove('animate-pulse');
      }
      textFields.forEach((field) => {
        field.classList.remove('animate-pulse');
      });
    }, 4000);
  }
}

// Make updateLocationUI, setStoreCookie, and getStoreCookiePayload available to SchedulerApp
window.updateLocationUI = updateLocationUI;
window.setStoreCookie = setStoreCookie;
window.getStoreCookiePayload = getStoreCookiePayload;

async function fetchClosestStore(coords) {
  const url = new URL(storeGeolocationEndpoint, window.location.origin);
  if (coords) {
    // 2 decimals is about 1 km - plenty for a 50-mile radius, without sending a precise location.
    url.searchParams.set('lat', coords.lat.toFixed(2));
    url.searchParams.set('lng', coords.lng.toFixed(2));
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Closest store geolocation response was not ok. Response status: ${response.status}`);
  }

  return response.json();
}

// Lookup state for readers of the cookie (Local Fitter Callout, CDP fitting). They await
// `ready` before reading it, since an older cookie may be cleared and re-fetched on this load.
let resolveReady;
const preferredStore = {
  status: 'pending', // 'pending' | 'found' | 'none' | 'imprecise' | 'error'
  ready: new Promise((resolve) => {
    resolveReady = resolve;
  }),
  canLocateWithBrowser: () => Boolean(window.isSecureContext && navigator.geolocation),
  locateWithBrowser,
};
window.pxgPreferredStore = preferredStore;

// Status is set before the cookie is written so `preferred-store:change` listeners see it.
function saveLookupResult(data, source) {
  if (data.store) {
    preferredStore.status = 'found';
    setStoreCookie(getStoreCookiePayload(data.store, source));
    updateLocationUI(data.store.name, data.store.lat, data.store.lng, true);
  } else if (data.locationImprecise === true) {
    // Session cookie only, so the next visit tries the IP lookup again.
    preferredStore.status = 'imprecise';
    setStoreCookie(getNoStoreCookiePayload('imprecise'), null);
  } else {
    // No store within radius - remember that so we don't keep trying to geolocate.
    preferredStore.status = 'none';
    setStoreCookie(getNoStoreCookiePayload('none'));
  }
  capturePreferredStoreEvent('preferred_store_resolved', {
    status: preferredStore.status,
    source,
    location_name: data.store?.name || null,
  });
  return preferredStore.status;
}

function getBrowserPosition() {
  return new Promise((resolve, reject) => {
    if (!preferredStore.canLocateWithBrowser()) {
      reject(new Error('Geolocation is unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      reject,
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 },
    );
  });
}

function getPositionErrorReason(err) {
  if (err?.code === 1) return 'denied';
  if (err?.code === 2) return 'position_unavailable';
  if (err?.code === 3) return 'timeout';
  return 'unsupported';
}

// Only call from a click - asking on page load shows the permission prompt with no context.
// `surface` names the UI that asked, for PostHog. Resolves 'found' | 'none' | 'imprecise', or
// 'unavailable' when the shopper denies permission, the request times out, or the lookup fails.
async function locateWithBrowser({ surface = null } = {}) {
  capturePreferredStoreEvent('preferred_store_location_requested', { surface });

  let coords;
  try {
    coords = await getBrowserPosition();
  } catch (err) {
    const reason = getPositionErrorReason(err);
    console.warn('PXG Stores: browser location unavailable:', err);
    capturePreferredStoreEvent('preferred_store_location_result', { result: 'unavailable', reason, surface });
    return 'unavailable';
  }

  try {
    const result = saveLookupResult(await fetchClosestStore(coords), 'browser');
    capturePreferredStoreEvent('preferred_store_location_result', { result, surface });
    return result;
  } catch (err) {
    console.error('PXG Stores error:', err);
    capturePreferredStoreEvent('preferred_store_location_result', {
      result: 'unavailable',
      reason: 'lookup_failed',
      surface,
    });
    return 'unavailable';
  }
}

async function initPreferredStore() {
  let storeData = getStoreFromCookie();

  if (storeData && storeData.v !== storeCookieVersion) {
    clearStoreCookie();
    storeData = null;
  }

  // We already know there's no usable store for this shopper
  if (storeData?.name === 'n/a') {
    preferredStore.status = storeData.reason === 'imprecise' ? 'imprecise' : 'none';
    registerPreferredStore(storeData);
    return;
  }

  // If the cookie holding closest store data exists, update the DOM immediately and return
  if (storeData?.name) {
    preferredStore.status = 'found';
    registerPreferredStore(storeData);
    updateLocationUI(storeData.name, storeData.latitude, storeData.longitude);
    return;
  }

  // Otherwise - proxy API request for closest PXG store within 50 miles, by IP
  try {
    saveLookupResult(await fetchClosestStore(), 'ip');
  } catch (err) {
    preferredStore.status = 'error';
    registerPreferredStore(null, 'error');
    capturePreferredStoreEvent('preferred_store_resolved', { status: 'error', source: 'ip', location_name: null });
    console.error('PXG Stores error:', err);
  }
}

// Run the geolocation script when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  initPreferredStore().finally(() => resolveReady(preferredStore.status));
});
